import { useEffect, useRef, useState } from 'react'
import { Undo2, X } from 'lucide-react'
import {
  pdfBase64ToObjectUrl,
  timetableDataQualityApi,
  type CanonicalValue,
  type TeacherContextEntry,
  type TeacherContextPdf,
} from '@/services/api/timetableDataQuality'
import { ApiErrorResponse } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'
import { ProgressDialog } from '@/components/common/ProgressDialog'
import { spanActionLabel } from '@/utils/spanAction'
import { adjacentSlot, fusionNeighbor, type SchoolSlot } from '@/utils/spanNeighbor'
import { PdfTimetableModal } from '@/components/dataquality/PdfTimetableModal'

const DAY = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

export const REASON_LABELS: Record<string, string> = {
  pdf_subject_lost: 'Fach im PDF, in der Lektion leer',
  name_without_id: 'Name ohne Fach-ID',
  id_name_mismatch: 'Name und Fach-ID passen nicht',
  missing_subject: 'Fach fehlt',
  concat_dup: 'Klasse verdoppelt',
  span_mismatch: 'Double/Triple passt nicht zum PDF',
  slot_shift: 'Slot verrutscht',
  empty_subject_both: 'PDF und System ohne Fach',
}

export type ScanFinding = {
  entry_id: string
  day_of_week: number | null
  lesson_number: number | null
  color: string
  reasons: string[]
  subject_name: string | null
  klass_name: string | null
  room_name: string | null
  pdf_raw_text: string | null
  pdf_had_subject: boolean | null
  pdf_span_start: number | null
  pdf_span_end: number | null
  suggested_action: 'assign_subject' | 'confirm_empty' | 'split_concat' | 'reimport' | null
  hole_key_usable: boolean
  same_holes_teacher: number
  same_holes_school: number
}

interface FindingFixPanelProps {
  teacherId: string
  teacherName: string
  finding: ScanFinding
  batchId: string | null
  importAvailable: boolean
  onClose: () => void
  onApplied: (result: { batch_id: string | null; affected_rows: number; undo_id?: string | null }) => void | Promise<void>
  onNext: () => void
  onReimport: () => void
}

export function FindingFixPanel({
  teacherId,
  teacherName,
  finding,
  batchId,
  importAvailable,
  onClose,
  onApplied,
  onNext,
  onReimport,
}: FindingFixPanelProps) {
  const [subjects, setSubjects] = useState<CanonicalValue[]>([])
  const [targetId, setTargetId] = useState('')
  const [applySame, setApplySame] = useState(true)
  const [scope, setScope] = useState<'teacher' | 'school'>('teacher')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pdfOpen, setPdfOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfMeta, setPdfMeta] = useState<TeacherContextPdf | null>(null)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [entries, setEntries] = useState<TeacherContextEntry[]>([])
  const [slots, setSlots] = useState<SchoolSlot[]>([])
  const [undoId, setUndoId] = useState<string | null>(null)
  const pdfUrlRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    timetableDataQualityApi.getCanonicalValues('subject').then((r) => {
      if (!cancelled) setSubjects(r.values)
    }).catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    setTargetId('')
    setApplySame(finding.hole_key_usable)
    setScope('teacher')
    setError(null)
  }, [finding.entry_id])

  useEffect(() => {
    let cancelled = false
    setPdfLoading(true)
    setPdfUrl(null)
    setPdfMeta(null)
    setEntries([])
    setSlots([])
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current)
      pdfUrlRef.current = null
    }
    void (async () => {
      try {
        const ctx = await timetableDataQualityApi.getTeacherContext(teacherId, batchId ?? undefined)
        if (cancelled) return
        setEntries(ctx.entries)
        setSlots(ctx.slots ?? [])
        setPdfMeta(ctx.pdf)
        if (!ctx.pdf?.page) {
          setPdfLoading(false)
          return
        }
        const page = await timetableDataQualityApi.getPdfPage(ctx.pdf.file_id, ctx.pdf.page)
        if (page.page_number !== ctx.pdf.page) {
          if (!cancelled) setPdfLoading(false)
          return
        }
        const url = pdfBase64ToObjectUrl(page.pdf_base64)
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current)
        pdfUrlRef.current = url
        setPdfUrl(url)
      } catch (err) {
        if (!cancelled) {
          if (!(err instanceof ApiErrorResponse && err.statusCode === 422)) {
            setError('PDF konnte nicht geladen werden')
          }
        }
      } finally {
        if (!cancelled) setPdfLoading(false)
      }
    })()
    return () => {
      cancelled = true
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current)
        pdfUrlRef.current = null
      }
    }
  }, [teacherId, batchId])

  const spanEntry = entries.find((row) => row.id === finding.entry_id) ?? null
  const upSlot = spanEntry ? adjacentSlot(slots, spanEntry.time_slot_id, 'up') : null
  const downSlot = spanEntry ? adjacentSlot(slots, spanEntry.time_slot_id, 'down') : null
  const neighborUp = spanEntry ? fusionNeighbor(entries, spanEntry, 'up', slots) : null
  const neighborDown = spanEntry ? fusionNeighbor(entries, spanEntry, 'down', slots) : null
  const labelEntry = spanEntry ?? { id: finding.entry_id, lesson_number: finding.lesson_number }

  const sameCount = scope === 'school' ? finding.same_holes_school : finding.same_holes_teacher
  const sentence = finding.reasons.map((r) => REASON_LABELS[r] || r).join(' · ')

  const run = async (action: ScanFinding['suggested_action']) => {
    if (!action || action === 'reimport' || busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await timetableDataQualityApi.resolveFinding({
        entry_id: finding.entry_id,
        action,
        target_id: action === 'assign_subject' ? targetId || undefined : undefined,
        apply_same_holes: applySame && finding.hole_key_usable,
        same_holes_scope: scope,
      })
      await onApplied({
        batch_id: result.batch_id,
        affected_rows: result.affected_rows,
      })
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Änderung fehlgeschlagen' }))
    } finally {
      setBusy(false)
    }
  }

  const extendSpan = async (direction: 'up' | 'down') => {
    if (busy || !(direction === 'up' ? (neighborUp || upSlot) : (neighborDown || downSlot))) return
    setBusy(true)
    setError(null)
    try {
      const result = await timetableDataQualityApi.extendDouble({ entry_id: finding.entry_id, direction })
      if (result.undo_id) setUndoId(result.undo_id)
      await onApplied({ batch_id: null, affected_rows: 1, undo_id: result.undo_id })
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Erweitern fehlgeschlagen' }))
    } finally {
      setBusy(false)
    }
  }

  const undoSpan = async () => {
    if (!undoId || busy) return
    setBusy(true)
    setError(null)
    try {
      await timetableDataQualityApi.undoExtend({ undo_id: undoId })
      setUndoId(null)
      await onApplied({ batch_id: null, affected_rows: 1 })
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Rückgängig fehlgeschlagen' }))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Lücke: {teacherName}</h3>
            <p className="text-sm text-gray-500">
              {DAY[finding.day_of_week ?? 0] ?? ''} L{finding.lesson_number ?? '?'} · {sentence}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void undoSpan()}
              disabled={!undoId || busy}
              className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-40"
            >
              <Undo2 className="w-4 h-4" />
              Rückgängig
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 p-4 overflow-auto flex-1 min-h-0">
          <div className="text-sm space-y-2">
            <p className="font-medium">System</p>
            <p>Fach: {finding.subject_name || '—'}</p>
            <p>Klasse: {finding.klass_name || '—'}</p>
            <p>Raum: {finding.room_name || '—'}</p>
            <p className="text-xs text-gray-500 font-mono break-all">PDF-Text: {finding.pdf_raw_text || '—'}</p>
          </div>
          <div className="min-h-[240px] border rounded">
            {pdfLoading ? (
              <p className="text-sm text-gray-500 p-4">Lade PDF…</p>
            ) : pdfUrl ? (
              <iframe src={pdfUrl} title="PDF" className="w-full h-full min-h-[240px]" />
            ) : !pdfMeta ? (
              <p className="text-sm text-gray-500 p-4">
                Kein PDF-Stundenplan vorhanden (weder Import-Mapping noch Upload der Kategorie „Stundenplan“).
              </p>
            ) : (
              <p className="text-sm text-gray-500 p-4">
                Seite unbekannt — das Sammel-PDF wird nicht geöffnet.
              </p>
            )}
          </div>
        </div>
        {error && <div className="mx-4 mb-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        <div className="p-4 border-t space-y-3">
          {finding.hole_key_usable && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={applySame} onChange={(e) => setApplySame(e.target.checked)} />
              Gleiche Löcher mitziehen
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as 'teacher' | 'school')}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value="teacher">dieser Lehrer ({finding.same_holes_teacher})</option>
                <option value="school">alle Lehrer ({finding.same_holes_school})</option>
              </select>
              {applySame && (
                <span className="text-gray-500">trifft {sameCount} Lektionen</span>
              )}
            </label>
          )}
          {finding.suggested_action === 'assign_subject' && (
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="border rounded px-3 py-2 text-sm min-w-[200px]"
              >
                <option value="">Schulfach wählen…</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <button
                disabled={!targetId || busy}
                onClick={() => void run('assign_subject')}
                className="px-3 py-2 text-sm text-white bg-blue-600 rounded disabled:opacity-50"
              >
                Fach zuordnen
              </button>
            </div>
          )}
          {finding.suggested_action === 'confirm_empty' && (
            <button
              disabled={busy}
              onClick={() => void run('confirm_empty')}
              className="px-3 py-2 text-sm text-white bg-blue-600 rounded"
            >
              Als leer bestätigen
            </button>
          )}
          {finding.suggested_action === 'split_concat' && (
            <button
              disabled={busy}
              onClick={() => void run('split_concat')}
              className="px-3 py-2 text-sm text-white bg-blue-600 rounded"
            >
              Klasse auftrennen
            </button>
          )}
          {finding.reasons.includes('span_mismatch') && (
            <div className="flex flex-wrap gap-2">
              <button
                disabled={busy || (!neighborUp && !upSlot)}
                onClick={() => void extendSpan('up')}
                className="px-3 py-2 text-sm border rounded disabled:opacity-50"
              >
                {spanActionLabel('up', labelEntry, neighborUp)}
              </button>
              <button
                disabled={busy || (!neighborDown && !downSlot)}
                onClick={() => void extendSpan('down')}
                className="px-3 py-2 text-sm text-white bg-blue-600 rounded disabled:opacity-50"
              >
                {spanActionLabel('down', labelEntry, neighborDown)}
              </button>
            </div>
          )}
          {finding.suggested_action === 'reimport' && (
            <button
              disabled={!importAvailable}
              onClick={onReimport}
              className="px-3 py-2 text-sm text-white bg-amber-600 rounded disabled:opacity-50"
            >
              Neu importieren
            </button>
          )}
          <div className="flex justify-between">
            <button
              disabled={busy}
              onClick={() => setPdfOpen(true)}
              className="text-sm text-blue-600 hover:underline disabled:opacity-50"
            >
              PDF grösser
            </button>
            <button
              disabled={busy}
              onClick={onNext}
              className="px-3 py-2 text-sm border rounded disabled:opacity-50"
            >
              Nächste Lücke
            </button>
          </div>
        </div>
      </div>
      {(busy || pdfLoading) && (
        <ProgressDialog
          title={busy ? 'Lücke wird geschlossen…' : 'PDF wird geladen…'}
          current={0}
          total={0}
        />
      )}
      {pdfOpen && !busy && (
        <PdfTimetableModal
          teacherId={teacherId}
          batchId={batchId}
          existingUrl={pdfUrl}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </div>
  )
}
